/**
 * The review's doubts as plain yes/no questions about one page (bug fix 1, spec 6 §2). Pure.
 *
 * A doubt about the whole mapping — the offset agreed on only some sample pages, could not be
 * worked out, or chapters were found by heading size — is asked once, about three sample
 * chapters. "No" turns it into per-chapter checking, one chapter at a time, each correction
 * re-applied to every chapter after it, so one wrong offset is fixed in an answer or two. Any
 * other unsure chapter is asked about on its own. A gap of three or more pages between two
 * chapters asks whether those pages belong to one. Answers edit the chapter list through the
 * same rules as the row's own edits; a question never blocks "Make flashcards".
 */
import type { DetectedChapter, SourceDocument } from "./types";
import { UNCERTAIN_BELOW } from "./validate";

export type Question =
  | { kind: "book"; id: "book"; samples: DetectedChapter[] }
  | {
      kind: "start";
      id: string;
      chapter: number;
      title: string;
      page: number;
      /** "4 of 28" while checking chapter by chapter. */
      counter?: { at: number; of: number };
    }
  | { kind: "title"; id: string; chapter: number }
  | { kind: "gap"; id: string; startPage: number; endPage: number; before: number; after: number };

export type Reply =
  | { kind: "yes" }
  | { kind: "no" }
  | { kind: "startsOn"; page: number }
  | { kind: "leaveOut" }
  | { kind: "addTo"; chapter: number };

/** What has been answered. `book`: the whole-book answer, null until given. */
export type Answered = { book: "yes" | "no" | null; done: string[] };
export const NOTHING_ANSWERED: Answered = { book: null, done: [] };

const BOOK_CODES = ["offset_majority", "uncalibrated", "headings"];
export const GAP_MIN = 3;

const ordered = (doc: SourceDocument) =>
  [...doc.chapters].sort((a, b) => a.startPage - b.startPage);
const included = (doc: SourceDocument) => ordered(doc).filter((c) => c.included !== false);

/** One doubt about the whole book: the code says so, or (a book detected before codes) the
 * evidence does, as describeEvidence reads it. */
export function bookDoubt(doc: SourceDocument): boolean {
  const e = doc.evidence;
  if (!e) return false;
  if (e.scope) return e.scope === "book";
  if (e.reasonCode) return BOOK_CODES.includes(e.reasonCode);
  if (e.method === "headings") return true;
  if (e.method !== "contents") return false;
  return e.offset === null || e.samples.some((s) => !s.agrees);
}

const unsure = (c: DetectedChapter) => c.confidence <= UNCERTAIN_BELOW;

/** First, middle and last of the chapters being made. */
function samples(doc: SourceDocument): DetectedChapter[] {
  const cs = included(doc);
  if (cs.length <= 3) return cs;
  return [cs[0]!, cs[Math.floor((cs.length - 1) / 2)]!, cs[cs.length - 1]!];
}

export function questionsFor(doc: SourceDocument, answered: Answered): Question[] {
  const done = new Set(answered.done);
  const out: Question[] = [];
  const cs = included(doc);
  const whole = bookDoubt(doc) && cs.some(unsure);

  if (whole && answered.book === null)
    out.push({ kind: "book", id: "book", samples: samples(doc) });
  if (whole && answered.book === "no") {
    // Chapter by chapter, in page order: only the next one to check is asked.
    const left = cs.filter((c) => !done.has(`start:${c.number}`));
    const next = left[0];
    if (next)
      out.push({
        kind: "start",
        id: `start:${next.number}`,
        chapter: next.number,
        title: next.title,
        page: next.startPage,
        counter: { at: cs.length - left.length + 1, of: cs.length },
      });
  }
  if (!whole) {
    for (const c of cs)
      if (unsure(c) && !done.has(`start:${c.number}`))
        out.push({
          kind: "start",
          id: `start:${c.number}`,
          chapter: c.number,
          title: c.title,
          page: c.startPage,
        });
  }
  for (const c of cs)
    if (c.title.trim() === "")
      out.push({ kind: "title", id: `title:${c.number}`, chapter: c.number });

  // Pages between two chapters that no chapter or labelled gap explains.
  const all = ordered(doc);
  const labelled = doc.gaps ?? [];
  for (let i = 1; i < all.length; i++) {
    const a = all[i - 1]!;
    const b = all[i]!;
    const start = a.endPage + 1;
    const end = b.startPage - 1;
    if (end - start + 1 < GAP_MIN) continue;
    // A labelled gap ("End of chapter questions") is left out by default: nothing to ask.
    if (labelled.some((g) => g.startPage <= start && g.endPage >= end)) continue;
    const id = `gap:${start}`;
    if (!done.has(id))
      out.push({
        kind: "gap",
        id,
        startPage: start,
        endPage: end,
        before: a.number,
        after: b.number,
      });
  }
  return out;
}

const settle = (c: DetectedChapter): DetectedChapter => ({ ...c, confidence: 1 });

/** Apply an answer: the edited list, and what is now answered. */
export function answer(
  doc: SourceDocument,
  answered: Answered,
  q: Question,
  reply: Reply,
): { doc: SourceDocument; answered: Answered } {
  const mark = (state: Answered) => ({ ...state, done: [...state.done, q.id] });
  if (q.kind === "book") {
    if (reply.kind === "yes")
      // Every chapter was placed by the same mapping: one yes clears them all.
      return {
        doc: { ...doc, chapters: doc.chapters.map(settle) },
        answered: { ...answered, book: "yes" },
      };
    return { doc, answered: { ...answered, book: "no" } };
  }
  if (q.kind === "start") {
    if (reply.kind !== "startsOn")
      return {
        doc: {
          ...doc,
          chapters: doc.chapters.map((c) => (c.number === q.chapter ? settle(c) : c)),
        },
        answered: mark(answered),
      };
    return {
      doc: moveStart(doc, q.chapter, reply.page, answered.book === "no"),
      answered: mark(answered),
    };
  }
  if (q.kind === "gap" && reply.kind === "addTo") {
    return {
      doc: {
        ...doc,
        chapters: doc.chapters.map((c) =>
          c.number === reply.chapter
            ? c.number === q.before
              ? { ...c, endPage: q.endPage }
              : { ...c, startPage: q.startPage }
            : c,
        ),
      },
      answered: mark(answered),
    };
  }
  return { doc, answered: mark(answered) };
}

/**
 * Chapter ``number`` starts on ``page``; the chapter before ends the page before. Checking
 * chapter by chapter, the same shift is a wrong offset, so every later chapter moves with it.
 */
export function moveStart(
  doc: SourceDocument,
  number: number,
  page: number,
  shiftLater: boolean,
): SourceDocument {
  const cs = ordered(doc);
  const at = cs.findIndex((c) => c.number === number);
  if (at < 0) return doc;
  const shift = page - cs[at]!.startPage;
  const clamp = (p: number) => Math.max(1, Math.min(doc.pageCount, p));
  const next = cs.map((c, i) => {
    if (i === at) return { ...settle(c), startPage: clamp(page) };
    if (shiftLater && i > at) return { ...c, startPage: clamp(c.startPage + shift) };
    return c;
  });
  // Every end follows the next chapter's start; the last keeps its own, clamped.
  const ends = next.map((c, i) => {
    const following = next[i + 1];
    if (following && (i === at - 1 || (shiftLater && i >= at)))
      return { ...c, endPage: Math.max(c.startPage, following.startPage - 1) };
    if (!following && shiftLater && i >= at) return { ...c, endPage: clamp(c.endPage + shift) };
    return c;
  });
  const byNumber = new Map(ends.map((c) => [c.number, c]));
  return { ...doc, chapters: doc.chapters.map((c) => byNumber.get(c.number) ?? c) };
}
